import { EntityLang } from '/@/modules/core/app/services/EntityLang';
import { getters } from '/@/modules/core/app/helpers/store';
import { EstateType } from '/@/modules/estate/enums/EstateType';
import { CurrentEstate } from '@/modules/estate/models/CurrentEstate';

export class EstateTypeLang extends EntityLang {
  get estateType(): string {
    return getters.auth.getCurrentEstate()?.type;
  }

  get estate(): CurrentEstate | undefined {
    return getters.auth.getCurrentEstate();
  }

  get assetShareOwnershipSoleDescription(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('estate.planner-is-only-owner');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('estate.deceased-was-only-owner');
    }
  }

  get assetShareOwnershipJointDescription(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('estate.planner-share-transferred');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('estate.deceased-share-transferred');
    }
  }

  get bankUploadDocumentDescription(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('asset.upload-statement');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('asset.upload-statement-date-of-death');
    }
  }

  get valueAtDateOfDeath(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('asset.market-value');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('asset.value-at-date-of-death');
    }
  }

  get estateValueSubLabel(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_WILL.key:
      case EstateType.CLEAR_VAULT.key:
        return this.t('estate.your-share-is');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('estate.estate-share-is');
    }
  }

  get stageCompletedMessage(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('guidancestep.carry-on-planning');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('guidancestep.carry-on-settling');
    }
  }

  get prefixTestateDistributionBannerMessage(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('estatedistribution.distributed-following-your');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('estatedistribution.distributed-following-will');
    }
  }

  get suffixTestateDistributionBannerMessage(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('estatedistribution.distribution-suffix-planning');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('estatedistribution.distribution-suffix-settlement');
    }
  }

  get intestateDistributionBannerMessage(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('estatedistribution.intestate-upload-will');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('estatedistribution.this-the-to');
    }
  }

  get rentalIncomeDescription(): string | null {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('asset.income-earned-planner');
      case EstateType.CLEAR_ESTATE.key:
        return this.t('asset.income-earned-owned');
      default:
        return null;
    }
  }

  get realEstatePrimaryResidenceDescription(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('asset.primary-residence-description');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('liability.where-was-living');
    }
  }

  get stageOfEstateCompletedText(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('guidancestep.stage-of-planning');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('guidancestep.stage-of-estate');
    }
  }

  get guardianNomination(): string {
    switch (this.estateType) {
      case EstateType.CLEAR_VAULT.key:
      case EstateType.CLEAR_WILL.key:
        return this.t('nomination.guardian');
      case EstateType.CLEAR_ESTATE.key:
      default:
        return this.t('nomination.legal-guardian');
    }
  }
}

export default new EstateTypeLang();
